import type React from 'react';
import { PageTitle } from '@/hooks/usePageTitle';
import {
  ClosingBand,
  PageHero,
  QuestionList,
  QuestionSection,
  RequestAccessButton,
  TextLink,
} from '@/components/marketing/Sections';
import { media } from '@/content/media';

/* ==========================================================================
   FOR ARTISTS (route /for-performers). Two people, in this order
   (docs/plans/MARKETING_SITE.md, personas 2 and 3):

   1. The artist who books their own shows. They put the show together, which
      is the same role as the venue booker, so they get the booking, the
      advance, settling up, and running their own room at any gig.
   2. The act who was sent a link. They arrive holding it, and most will never
      make an account (CONTEXT.md, Guest), so that section answers "do I have
      to sign up?" before it sells anything. Home links straight to it
      (#a-link).

   Limits, checked in code before writing: a venue the artist adds that isn't
   on Showliner shows on the event and the calendar but brings no house
   currency to the room; an artist's blackouts mark their calendar and feed
   but don't raise a double-booking warning. Neither is promised here.
   ========================================================================== */

const ForPerformers: React.FC = () => (
  <>
    <PageTitle title="For artists" />
    <PageHero
      title="For artists who book their own shows"
      actions={
        <>
          <RequestAccessButton />
          <TextLink to="#a-link">Somebody sent you a link? ↓</TextLink>
        </>
      }
    >
      <p>
        Put the dates on a calendar, build the bill, get the details from everyone and settle up
        with the other acts, then run the room yourself at any gig, whether or not the venue is on
        Showliner.
      </p>
    </PageHero>

    <QuestionList>
      <QuestionSection question="How do I book my own shows?">
        <p>
          Put the date on the calendar and add the venue, even if they aren't on Showliner. Add
          the acts you want on the bill and send each one a link by text, by email, or in the group
          chat. They answer on their phone without making an account, and if there's money on it
          you can send an offer with the terms written down: a flat guarantee, a split of the door,
          or a guarantee against or on top of a split.
        </p>
        <p>
          Block out the dates you can't play so they sit on your calendar, and subscribe to that
          calendar from your phone so the shows land next to everything else.
        </p>
      </QuestionSection>

      <QuestionSection question="How do I get the details from everyone?">
        <p>
          Post what you already know once, like load-in, set times and parking, then ask every act
          on the bill for what you need from them, like an input list or how many people they're
          bringing. They answer on the same link, and the show page shows what's still outstanding
          and who it's waiting on.
        </p>
        <p>
          After the show, enter what the door did. Showliner works out what each act is owed from
          the terms you agreed and sends it to them to sign off on their phone. You pay them however
          you already do, and mark it paid.
        </p>
      </QuestionSection>

      <QuestionSection
        question="Can I run the room at a venue that isn't on Showliner?"
        media={media.phoneRoom}
        frame="phone"
        caption="A fan's phone, in the room"
        flip
      >
        <p>
          Yes. Choose Start now, then Live show, and you're on the bill of your own room. Put your
          QR code on the merch table, or on the bar's TV if they'll let you use it, and fans who
          scan it land in the room with no app and no account.
        </p>
        <p>
          They chat, tip you with real money (you keep the whole tip, paid out through Stripe), and
          request songs off your list. They also earn Cheer just by being there, and it follows them
          from room to room, so the people who come to every show have something to show for it.
        </p>
      </QuestionSection>

      <QuestionSection
        question="What about my stage plot?"
        media={media.adminStagePlot}
        frame="browser"
        caption="An act's plot on top of the venue's backline"
      >
        <p>
          Draw it once and save it as a template, one for the full band, one for acoustic, one for
          the solo set. When a venue has drawn its backline, yours goes on top of it, so you can see
          what's already on the stage before you load in. It exports as a PDF tech rider for venues
          that aren't on Showliner.
        </p>
      </QuestionSection>

      <QuestionSection question="Who's playing tonight?">
        <p>
          List who's in the band, mark who plays each show, and share tonight's set list and charts
          with them, including the sub who's never seen them before. The venue sees who's coming and
          never sees your set list.
        </p>
      </QuestionSection>

      <QuestionSection
        question="What do I get on my page?"
        media={media.profile}
        frame="phone"
        caption="An act's page, demo data"
        flip
      >
        <p>
          Your bio and your links, and a list of upcoming shows if you want one, which fills in as
          you and the venues you play book dates. You also get a short link and a QR code for the
          merch table that opens your page, or your room if you're playing right now.
        </p>
      </QuestionSection>

      <QuestionSection
        id="a-link"
        question="Somebody sent you a Showliner link?"
        media={media.phoneInvite}
        frame="phone"
        caption="An invite, opened on the act's phone"
      >
        <p>
          It's from a venue or another act putting a show together. It has the date and the venue,
          the rest of the bill and where you are in it, and anything they need from you before the
          show. If there's an offer, the terms are there too.
        </p>
        <p>
          You don't need an account to answer it. Agreeing to money asks for a code we email you,
          so we know it's you, and that's still not an account. Come back to the same link to change
          an answer, and after the show the settlement shows up there for you to sign off on.
        </p>
      </QuestionSection>

      <QuestionSection question="I'm crew, not a performer">
        <p>
          Sound engineers, photographers, lighting, video: you can make a profile too, get added to
          shows, and see the current plan for the night instead of a screenshot from three weeks
          ago. It's free, the same as it is for acts.
        </p>
      </QuestionSection>
    </QuestionList>

    <ClosingBand
      title="Free for artists booking a few shows a month"
      actions={
        <>
          <RequestAccessButton />
          <TextLink to="/pricing?for=performers">See plans →</TextLink>
        </>
      }
    >
      <p>
        Performer Pro is $7 a month, or $70 a year, for artists booking a lot of their own shows or
        running more than one project. Answering someone else's booking never costs anything and
        never counts toward a limit.
      </p>
    </ClosingBand>
  </>
);

export default ForPerformers;
